<template>
  <div class="w-full h-full bg-gray-50 flex items-center justify-center py-6 sm:py-8 lg:py-10">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4">
      <!-- Featured products 标题 -->
      <div class="text-center mb-8 sm:mb-10 lg:mb-12">
        <h2 class="text-xl sm:text-2xl font-bold text-[#2f5c45]">
          {{ t('ourCustomers.featuredProducts.title') }}
        </h2>
      </div>

      <!-- 产品网格 - 响应式布局 -->
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6 lg:gap-8 mb-8 sm:mb-10 lg:mb-12">
        <div
          v-for="(product, index) in productImages.slice(0, 6)"
          :key="index"
          class="product-item bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 flex flex-col items-center cursor-pointer transition-all duration-300 hover:shadow-md min-w-0"
          @mouseenter="handleProductHover(index)"
          @mouseleave="handleProductLeave()"
          @touchstart="handleProductHover(index)"
          @touchend="handleProductLeave()"
        >
          <div class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mb-2 sm:mb-3 lg:mb-4 overflow-hidden rounded-lg">
            <img
              :src="product.image"
              :alt="t('ourCustomers.featuredProducts.productType')"
              class="w-full h-full object-cover transition-transform duration-300"
              :class="{ 'scale-105': hoveredProduct === index }"
            />
          </div>
          <div class="text-center w-full">
            <p class="text-xs sm:text-sm font-medium text-gray-800 mb-1 truncate">
              {{ t('ourCustomers.featuredProducts.productType') }}
            </p>
            <p class="text-xs text-[#4CAF50] flex items-center justify-center truncate">
              {{ t('ourCustomers.featuredProducts.category') }}
              <span class="ml-1 text-[#4CAF50]">💬</span>
            </p>
          </div>
        </div>
      </div>

      <!-- 产品网格 - 第二行 -->
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
        <div
          v-for="(product, index) in productImages.slice(6, 12)"
          :key="index + 6"
          class="product-item bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 flex flex-col items-center cursor-pointer transition-all duration-300 hover:shadow-md min-w-0"
          @mouseenter="handleProductHover(index + 6)"
          @mouseleave="handleProductLeave()"
          @touchstart="handleProductHover(index + 6)"
          @touchend="handleProductLeave()"
        >
          <div class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mb-2 sm:mb-3 lg:mb-4 overflow-hidden rounded-lg">
            <img
              :src="product.image"
              :alt="t('ourCustomers.featuredProducts.productType')"
              class="w-full h-full object-cover transition-transform duration-300"
              :class="{ 'scale-105': hoveredProduct === index + 6 }"
            />
          </div>
          <div class="text-center w-full">
            <p class="text-xs sm:text-sm font-medium text-gray-800 mb-1 truncate">
              {{ t('ourCustomers.featuredProducts.productType') }}
            </p>
            <p class="text-xs text-[#4CAF50] flex items-center justify-center truncate">
              {{ t('ourCustomers.featuredProducts.category') }}
              <span class="ml-1 text-[#4CAF50]">💬</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()

  const hoveredProduct = ref<number | null>(null)

  const handleProductHover = (index: number) => {
    hoveredProduct.value = index
  }

  const handleProductLeave = () => {
    hoveredProduct.value = null
  }

  const getImagePath = (imageNumber: number) => {
    try {
      // 使用动态导入来获取图片
      return new URL(
        `../../../assets/525565/${imageNumber}.png`,
        import.meta.url
      ).href
    } catch (error) {
      console.warn(`Image not found: 525565/${imageNumber}.png`)
      // 返回默认图片或占位符
      return new URL('../../../assets/525565/1.png', import.meta.url).href
    }
  }

  // 使用包装产品相关的图片，更符合图一的实际产品展示
  const productImages = [
    // 第一行：纸袋、包装盒、食品包装等
    {
      image: getImagePath(1)
    }, // 纸袋
    {
      image: getImagePath(2)
    }, // 包装盒
    {
      image: getImagePath(3)
    }, // 食品包装
    {
      image: getImagePath(4)
    }, // 信封包装
    {
      image: getImagePath(5)
    }, // 快递包装
    {
      image: getImagePath(6)
    }, // 纸袋2

    // 第二行：杯子、碗、包装盒等
    {
      image: getImagePath(7)
    }, // 纸杯
    {
      image: getImagePath(8)
    }, // 碗
    {
      image: getImagePath(9)
    }, // 白色包装
    {
      image: getImagePath(10)
    }, // 包装盒
    {
      image: getImagePath(11)
    }, // 彩色包装
    {
      image: getImagePath(12)
    } // 纸袋3
  ]
</script>

<style scoped>
  .product-item:hover {
    transform: translateY(-2px);
  }
  
  /* 移动端触摸优化 */
  @media (max-width: 1024px) {
    .product-item:active {
      transform: translateY(-1px);
      transition: transform 0.1s ease;
    }
  }
</style>
